import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Printing.css";

const ORDER_API = "https://okna.co.tz/mrms/Orders.php";

export default function Printing() {
    const token = localStorage.getItem("token");
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [staff, setStaff] = useState({ full_name: "Staff" });

    useEffect(() => {
        if (!token) {
            Swal.fire("Session Expired", "Please login again", "warning");
            return;
        }
        
        const staffInfo = localStorage.getItem("staff");
        if (staffInfo) {
            try {
                setStaff(JSON.parse(staffInfo));
            } catch (e) {
                console.error("Failed to parse staff info");
            }
        }
        
        loadTodayOrders();
        
        const interval = setInterval(() => {
            loadTodayOrders(true);
        }, 30000);
        
        return () => clearInterval(interval);
    }, [token]);

    const loadTodayOrders = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const orderRes = await fetch(`${ORDER_API}?admin=true&token=${token}`, {
                method: "GET",
                headers: { 
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (orderRes.status === 401) {
                localStorage.removeItem("token");
                localStorage.removeItem("staff");
                Swal.fire("Session Expired", "Please login again", "warning");
                window.location.href = "/";
                return;
            }

            const orderData = await orderRes.json();
            
            if (Array.isArray(orderData)) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                const todayOrders = orderData.filter(order => {
                    const orderDate = new Date(order.created_at);
                    orderDate.setHours(0, 0, 0, 0);
                    return orderDate.getTime() === today.getTime();
                });
                
                const sortedOrders = todayOrders.sort((a, b) => 
                    new Date(b.created_at) - new Date(a.created_at)
                );
                
                const ordersWithItems = [];
                for (const order of sortedOrders) {
                    try {
                        const detailRes = await fetch(`${ORDER_API}?id=${order.id}&token=${token}`, {
                            headers: { 
                                "Authorization": `Bearer ${token}`,
                                "Content-Type": "application/json"
                            }
                        });

                        if (detailRes.status === 401) {
                            throw new Error("Authentication failed");
                        }

                        const detail = await detailRes.json();
                        
                        // Ensure items is always an array
                        const items = Array.isArray(detail.items) ? detail.items : [];
                        console.log(`Order ${order.order_number} has ${items.length} items:`, items);
                        
                        ordersWithItems.push({
                            id: order.id,
                            order_number: order.order_number,
                            total_amount: order.total_amount || 0,
                            created_at: order.created_at,
                            staff_name: order.staff_name || "Unknown",
                            items: items,
                            item_count: items.length
                        });
                    } catch (err) {
                        console.error(`Error fetching order ${order.id}:`, err);
                        ordersWithItems.push({ 
                            ...order, 
                            items: [], 
                            item_count: 0 
                        });
                    }
                }
                setOrders(ordersWithItems);
                console.log("Loaded orders:", ordersWithItems);
            } else if (orderData.error) {
                if (!silent) Swal.fire("Error", orderData.error, "error");
            }
        } catch (err) {
            console.error("Load error:", err);
            if (!silent) Swal.fire("Error", "Server not reachable", "error");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const viewOrder = (order) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const printReceipt = (order) => {
        const total = order.total_amount || 0;
        
        const orderDate = new Date(order.created_at);
        const date = orderDate.toLocaleDateString('en-GB');
        const time = orderDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        const day = orderDate.toLocaleDateString('en-US', { weekday: 'long' });

        const itemsHTML = (order.items || []).map(item => `
            <tr>
                <td style="padding: 8px 0;">${item.item_name || "Unknown"}</td>
                <td style="padding: 8px 0; text-align: center;">${item.quantity || 0}</td>
                <td style="padding: 8px 0; text-align: right;">${((item.price || 0) * (item.quantity || 0)).toLocaleString()}</td>
            </tr>
        `).join("");

        const receiptHTML = `
<!DOCTYPE html>
<html>
<head>
    <title>Receipt #${order.order_number}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 380px;
            margin: 0 auto;
            background: #f5f5f5;
        }
        .receipt {
            background: white;
            border-radius: 10px;
            padding: 20px;
            border: 2px solid #2d6187;
        }
        .header {
            text-align: center;
            margin-bottom: 20px;
        }
        .name {
            color: #2d6187;
            font-size: 20px;
            font-weight: 700;
            margin: 5px 0;
        }
        .address {
            font-size: 12px;
            color: #555;
            margin: 3px 0;
            line-height: 1.4;
        }
        hr {
            border: none;
            border-top: 2px dashed #2d6187;
            margin: 15px 0;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
        }
        th {
            text-align: left;
            padding: 10px 0;
            border-bottom: 2px solid #2d6187;
            color: #2d6187;
        }
        td {
            padding: 8px 0;
            font-size: 13px;
        }
        th:last-child, td:last-child {
            text-align: right;
        }
        th:nth-child(2), td:nth-child(2) {
            text-align: center;
        }
        .total-row {
            font-size: 16px;
            font-weight: 800;
            text-align: right;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 2px dashed #2d6187;
        }
        .info-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            font-size: 13px;
            margin: 10px 0;
        }
        .info-item {
            margin: 5px 0;
        }
        @media print {
            body {
                background: white;
                padding: 0;
            }
            .receipt {
                border: 1px solid #ddd;
            }
        }
    </style>
</head>
<body>
    <div class="receipt">
        <div class="header">
            <div class="name">MARAFIKI RESTAURANT</div>
            <div class="address">30-Gerezani Street-Posta, SGR Train Station-3rd Floor</div>
            <div class="address">Dar es Salaam, Tanzania</div>
            <div class="address">TEL: +255 793 888 888</div>
        </div>
        
        <hr/>
        
        <div><strong>RECEIPT:</strong> ${order.order_number}</div>
        
        <div class="info-grid">
            <div class="info-item"><strong>Date:</strong> ${date}</div>
            <div class="info-item"><strong>Time:</strong> ${time}</div>
            <div class="info-item"><strong>Day:</strong> ${day}</div>
        </div>
        
        <div><strong>Waitress:</strong> ${order.staff_name || staff.full_name}</div>
        
        <table>
            <thead>
                <tr>
                    <th>ITEM</th>
                    <th>QTY</th>
                    <th>AMOUNT</th>
                </tr>
            </thead>
            <tbody>
                ${itemsHTML || '<tr><td colspan="3" style="text-align: center;">No items</td></tr>'}
            </tbody>
        </table>
        
        <div class="total-row"><strong>TOTAL:</strong> ${total.toLocaleString()} TZS</div>
    </div>
    <script>
        window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
        };
    </script>
</body>
</html>`;

        const printWindow = window.open('', '_blank', 'width=400,height=600');
        if (printWindow) {
            printWindow.document.write(receiptHTML);
            printWindow.document.close();
            printWindow.focus();
        } else {
            Swal.fire("Error", "Please allow pop-ups to print receipts", "error");
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatCurrency = (amount) => {
        return Number(amount || 0).toLocaleString() + ' TZS';
    };

    if (loading && orders.length === 0) {
        return (
            <div className="print-loading">
                <div className="print-spinner"></div>
                <p>Loading today's orders...</p>
            </div>
        );
    }

    return (
        <div className="print-container">
            {orders.length === 0 ? (
                <div className="print-empty">
                    <div className="empty-icon">📭</div>
                    <p>No orders found for today</p>
                    <button onClick={() => loadTodayOrders(false)} className="refresh-empty-btn">
                        Refresh
                    </button>
                </div>
            ) : (
                <div className="print-table-wrapper">
                    <table className="print-table">
                        <thead>
                            <tr>
                                <th>Order #</th>
                                <th>Items</th>
                                <th>Total</th>
                                <th>Time</th>
                                <th>Staff</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map(order => (
                                <tr key={order.id}>
                                    <td className="order-number">
                                        #{order.order_number}
                                    </td>
                                    <td className="items-preview">
                                        {order.items?.slice(0, 2).map((item, idx) => (
                                            <div key={idx} className="item-preview">
                                                {item.item_name} ×{item.quantity}
                                            </div>
                                        ))}
                                        {order.item_count > 2 && (
                                            <div className="more-items">
                                                +{order.item_count - 2} more
                                            </div>
                                        )}
                                        {order.item_count === 0 && (
                                            <div className="no-items">No items</div>
                                        )}
                                    </td>
                                    <td className="total">
                                        {formatCurrency(order.total_amount)}
                                    </td>
                                    <td className="time">
                                        {formatDate(order.created_at)}
                                    </td>
                                    <td className="staff">
                                        {order.staff_name}
                                    </td>
                                    <td className="actions">
                                        <button 
                                            onClick={() => viewOrder(order)} 
                                            className="view-btn"
                                        >
                                            View
                                        </button>
                                        <button 
                                            onClick={() => printReceipt(order)} 
                                            className="print-btn"
                                        >
                                            Print
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && selectedOrder && (
                <div className="print-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="print-modal" onClick={e => e.stopPropagation()}>
                        <div className="print-modal-header">
                            <h3>Order #{selectedOrder.order_number}</h3>
                            <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        
                        <div className="print-modal-body">
                            <div className="order-info-grid">
                                <div className="info-item">
                                    <strong>Staff:</strong>
                                    <span>{selectedOrder.staff_name}</span>
                                </div>
                                <div className="info-item">
                                    <strong>Time:</strong>
                                    <span>{formatDate(selectedOrder.created_at)}</span>
                                </div>
                                <div className="info-item">
                                    <strong>Total Items:</strong>
                                    <span>{selectedOrder.item_count}</span>
                                </div>
                            </div>

                            <div className="items-section">
                                <h4>Order Items</h4>
                                {selectedOrder.items?.length > 0 ? (
                                    <table className="items-table">
                                        <thead>
                                            <tr>
                                                <th>Item Name</th>
                                                <th>Qty</th>
                                                <th>Price</th>
                                                <th>Subtotal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedOrder.items.map((item, i) => (
                                                <tr key={i}>
                                                    <td>{item.item_name}</td>
                                                    <td className="text-center">{item.quantity}</td>
                                                    <td className="text-right">{formatCurrency(item.price)}</td>
                                                    <td className="text-right">{formatCurrency(item.subtotal)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="total-row">
                                                <td colSpan="3" className="text-right"><strong>Total:</strong></td>
                                                <td className="text-right"><strong>{formatCurrency(selectedOrder.total_amount)}</strong></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                ) : (
                                    <div className="no-items-modal">No items found for this order</div>
                                )}
                            </div>
                        </div>
                        
                        <div className="print-modal-footer">
                            <button 
                                className="print-receipt-btn"
                                onClick={() => {
                                    setShowModal(false);
                                    printReceipt(selectedOrder);
                                }}
                            >
                                🖨️ Print Receipt
                            </button>
                            <button 
                                className="close-modal-btn" 
                                onClick={() => setShowModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}